import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { en } from '../../translations/en';
import { logger } from '../../utils/logger';
import api from '../../utils/api';

interface CreatorIdentifier {
  platform: string;
  id: string;
}

export const CreatorIdentifiersSection = () => {
  const [identifiers, setIdentifiers] = useState<CreatorIdentifier[]>([]);
  const [newIdentifier, setNewIdentifier] = useState<CreatorIdentifier>({ platform: '', id: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    const fetchIdentifiers = async () => {
      try {
        const response = await api.get('/api/users/me/identifiers');
        setIdentifiers(response.data.identifiers || []);
      } catch (error) {
        logger.warn('Failed to load creator identifiers', error);
      }
    };
    fetchIdentifiers();
  }, []);

  const handleAdd = async () => {
    if (newIdentifier.platform && newIdentifier.id) {
      const updated = [...identifiers, newIdentifier];
      try {
        await api.put('/api/users/me/identifiers', { identifiers: updated });
        setIdentifiers(updated);
        setNewIdentifier({ platform: '', id: '' });
      } catch (err) {
        logger.warn('Failed to add creator identifier', err);
        setSnackbar({ open: true, message: 'Failed to add identifier. Please try again.' });
      }
    }
  };

  const handleRemove = async (index: number) => {
    const updated = identifiers.filter((_, i) => i !== index);
    try {
      await api.put('/api/users/me/identifiers', { identifiers: updated });
      setIdentifiers(updated);
    } catch (err) {
      logger.warn('Failed to remove creator identifier', err);
      setSnackbar({ open: true, message: 'Failed to remove identifier. Please try again.' });
    }
  };

  return (
    <Box>
      <List>
        {identifiers.map((identifier, index) => (
          <ListItem key={index} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
            <ListItemText primary={identifier.platform} secondary={identifier.id} />
            <ListItemSecondaryAction>
              <IconButton edge="end" onClick={() => handleRemove(index)} aria-label="Remove identifier">
                <Delete />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField
          select
          label={en.settings.account.identifiers.platform}
          value={newIdentifier.platform}
          onChange={(e) => setNewIdentifier({ ...newIdentifier, platform: e.target.value })}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="Getty">{en.settings.account.identifiers.platforms.getty}</MenuItem>
          <MenuItem value="ORCID">{en.settings.account.identifiers.platforms.orcid}</MenuItem>
          <MenuItem value="ISNI">{en.settings.account.identifiers.platforms.isni}</MenuItem>
          <MenuItem value="Other">{en.settings.account.identifiers.platforms.other}</MenuItem>
        </TextField>
        <TextField
          label="Identifier ID"
          value={newIdentifier.id}
          onChange={(e) => setNewIdentifier({ ...newIdentifier, id: e.target.value })}
          fullWidth
        />
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAdd}
          disabled={!newIdentifier.platform || !newIdentifier.id}
        >
          {en.settings.account.identifiers.add}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ open: false, message: '' })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: '' })}
          severity="error"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
